import { DELEGATE_SUPPORT_ROLE } from '../../../authz/delegation';
import type { ActionSet } from '../../../authz/types';
import {
	AISVG,
	ContactsSVG,
	DivisionsSVG,
	ElectionSVG,
	ElectiveCategorySVG,
	ImportVotersSVG,
	ManageAgentsSVG,
	MyElectionSVG,
	MyPartySVG,
	MyWalletSVG,
	OverviewSVG,
	PoliticalPartySVG,
	ResultAnalyticsSVG,
	ResultSVG,
	ResultVerificationSVG,
	VerificationRequestSVG,
	VoterInteractionSVG,
} from '../../../components/reusable/svgs';
import { CandidateRecordsSVG } from '../../../components/reusable/svgs/CandidateRecordsSVG';
import { IRevExtractionSVG } from '../../../components/reusable/svgs/IRevExtractionSVG';
import { MediaGallerySVG } from '../../../components/reusable/svgs/MediaGallerySVG';
import { MessageTemplateSVG } from '../../../components/reusable/svgs/MessageTemplateSVG';
import { ResultAuditSVG } from '../../../components/reusable/svgs/ResultAuditSVG';
import { ResultTimeMachineSVG } from '../../../components/reusable/svgs/ResultTimeMachineSVG';
import { SituationRoomSVG } from '../../../components/reusable/svgs/SituationRoomSVG';
import { pageurl } from '../../../constants/pageurl';
import { ACTIONS } from '../../permissions/actions';
import { ACTIONS_VESR } from '../../permissions/actions.vesr';
import type { IMenuItem } from '../../utils';

// Explicit head element so this satisfies the non-empty `ActionSet` tuple —
// `Object.values` alone widens to a plain array, which would let an empty gate through.
// Deduped because the head is already in the spread.
const ANY_VESR_ACTION = [...new Set([ACTIONS.VESR_DASHBOARD, ...Object.values(ACTIONS_VESR)])] as unknown as ActionSet;

// Items with `requiresElection: true` remain visible regardless of election context.
// Clicking one when no election is selected opens the election picker instead of navigating.
export const menuData: IMenuItem[] = [
	{
		icon: <OverviewSVG />,
		title: 'Overview',
		pageUrl: pageurl.OVERVIEW,
		roleActions: [ACTIONS.OVERVIEW, ACTIONS.OVERVIEW_PARTY_ADMIN, ACTIONS.OVERVIEW_PARTY_CANDIDATE],
		section: '',
	},
	{
		icon: <OverviewSVG />,
		title: 'Dashboard',
		pageUrl: pageurl.OBSERVER_DASHBOARD,
		roleActions: [ACTIONS.ELECTION_OBSERVERS_MANAGE],
		observerAdminOnly: true,
		section: '',
	},
	{
		icon: <MyElectionSVG />,
		title: 'My Election',
		pageUrl: pageurl.MYELECTION,
		roleActions: [ACTIONS.ELECTION_RESULT_ANALYTICS],
		requiresElection: true,
		section: 'My Campaign',
	},
	{
		icon: <MyElectionSVG />,
		title: 'Live Election',
		pageUrl: pageurl.LIVEELECTION,
		// Same audience as My Election. The page re-checks the live JWT role (Super Admin /
		// Party Admin / Candidate) because this action code is held more widely.
		roleActions: [ACTIONS.ELECTION_RESULT_ANALYTICS],
		requiresElection: true,
		section: 'My Campaign',
	},
	{
		icon: <MyElectionSVG />,
		title: 'Strong Hold',
		pageUrl: pageurl.STRONGHOLD,
		// Same audience and gate as Live Election, which reads the same plan; the page re-checks
		// the live JWT role. Write access is decided server-side (Super Admin is view-only).
		roleActions: [ACTIONS.ELECTION_RESULT_ANALYTICS],
		requiresElection: true,
		section: 'My Campaign',
	},
	{
		icon: <MyElectionSVG />,
		title: 'Cancelled units',
		pageUrl: pageurl.CANCELLED_UNITS,
		// Same audience and gate as Live Election; the page re-checks the live JWT roles (Super
		// Admin / Party Admin / Candidate) and hides the write actions from Candidates.
		roleActions: [ACTIONS.ELECTION_RESULT_ANALYTICS],
		requiresElection: true,
		section: 'My Campaign',
	},
	{
		icon: <MyPartySVG />,
		title: 'My Party',
		pageUrl: pageurl.MYPARTY,
		roleActions: [ACTIONS.ELECTION_RESULT_ANALYTICS],
		requiresElection: true,
		section: 'My Campaign',
	},
	{
		icon: <ManageAgentsSVG />,
		title: 'Support Desk',
		// BORROWED GATE. `support_desk` is declared in actions.support.ts but is NOT yet seeded
		// server-side by api.core's PermissionBootstrapHostedService, so gating on it directly
		// would hide this item from everyone including Super Admin -- the exact failure this
		// repo hit with `analyst_performance`. MANAGE_AGENTS is already granted to the same
		// audience (Super Admin / Party Admin / Candidate) and is the closest true statement of
		// who should see a desk for their agents.
		//
		// Swap to [ACTIONS.SUPPORT_DESK] once the action exists AND users have re-logged in --
		// TokenService stamps `action` claims at mint time, so an existing session never gains
		// a newly created key.
		pageUrl: pageurl.SUPPORT,
		roleActions: [ACTIONS.MANAGE_AGENTS],
		intendedRoleActions: [ACTIONS.SUPPORT_DESK],
		section: 'Operations',
	},
	{
		icon: <ManageAgentsSVG />,
		title: 'Manage Agents',
		pageUrl: pageurl.MANAGEAGENTS,
		roleActions: [ACTIONS.ELECTION_RESULT_ANALYTICS],
		requiresElection: true,
		section: 'Operations',
	},
	{
		icon: <ManageAgentsSVG />,
		title: 'Manage Observers',
		pageUrl: pageurl.MANAGE_OBSERVERS,
		roleActions: [ACTIONS.ELECTION_OBSERVERS_MANAGE],
		section: 'Operations',
	},
	{
		icon: <ManageAgentsSVG />,
		title: 'Observer Desk',
		pageUrl: pageurl.OBSERVER_DESK,
		roleActions: [ACTIONS.ELECTION_OBSERVERS_MANAGE],
		section: 'Operations',
	},
	{
		icon: <ResultAnalyticsSVG />,
		title: 'Result Analytics',
		pageUrl: pageurl.RESULTANALYTICS,
		roleActions: [ACTIONS.ELECTION_RESULT_ANALYTICS],
		requiresElection: true,
		section: 'Results',
	},
	{
		icon: <ResultVerificationSVG />,
		title: 'Result Verification',
		pageUrl: pageurl.RESULTVERIFICATION,
		roleActions: [ACTIONS.RESULT_VERIFICATION],
		requiresElection: true,
		section: 'Results',
	},
	{
		icon: <ResultAuditSVG />,
		title: 'Result Audit',
		// Surfaced via ELECTION_RESULT_ANALYTICS (already granted to Super Admin / Party Admin /
		// Candidate — the audit's audience) so the item shows before the dedicated `result_audit`
		// backend Action is seeded. The module's inline live-JWT role check is the real gate.
		// Swap to [ACTIONS.RESULT_AUDIT] once that action is created + granted server-side.
		pageUrl: pageurl.RESULTAUDIT,
		roleActions: [ACTIONS.ELECTION_RESULT_ANALYTICS],
		intendedRoleActions: [ACTIONS.RESULT_AUDIT],
		requiresElection: true,
		section: 'Results',
	},
	{
		icon: <ResultTimeMachineSVG />,
		title: 'Result Time Machine',
		// Surfaced via ELECTION_RESULT_ANALYTICS (already granted to Super Admin / Party Admin /
		// Candidate — the timeline's audience) so the item shows before the dedicated
		// `result_time_machine` backend Action is seeded. The module's inline live-JWT role
		// check is the real gate. Swap to [ACTIONS.RESULT_TIME_MACHINE] once that action exists.
		pageUrl: pageurl.RESULTTIMEMACHINE,
		roleActions: [ACTIONS.ELECTION_RESULT_ANALYTICS],
		intendedRoleActions: [ACTIONS.RESULT_TIME_MACHINE],
		requiresElection: true,
		section: 'Results',
	},
	{
		icon: <ResultSVG />,
		title: 'Election Results',
		pageUrl: pageurl.RESULTS,
		roleActions: [ACTIONS.ELECTION_RESULT],
		section: 'Results',
	},
	{
		icon: <IRevExtractionSVG />,
		title: 'IRev Extraction',
		pageUrl: pageurl.IREVEXTRACTION,
		roleActions: [ACTIONS.IREV_EXTRACTION],
		requiresElection: true,
		section: 'Results',
	},
	{
		icon: <IRevExtractionSVG />,
		title: 'Result Pipeline',
		pageUrl: pageurl.RESULTPIPELINE,
		// Borrows an existing action deliberately: `result_pipeline` is not seeded server-side
		// yet, and gating on an ungranted key would hide the module from everyone. Swap to
		// ACTIONS.RESULT_PIPELINE once it is seeded — same precedent as Result Audit.
		roleActions: [ACTIONS.ELECTION_RESULT_ANALYTICS],
		intendedRoleActions: [ACTIONS.RESULT_PIPELINE],
		requiresElection: true,
		section: 'Results',
	},
	{
		icon: <ResultVerificationSVG />,
		title: 'Analyst Performance',
		pageUrl: pageurl.ANALYSTPERFORMANCE,
		// Borrowed until `analyst_performance` is seeded server-side, but NOT the usual borrow.
		// ELECTION_RESULT_ANALYTICS — what Result Audit / Time Machine / Pipeline borrow — is held
		// by Party Admin and Candidate, and this page names individual staff and their overturn
		// rates. RESULT_VERIFICATION is narrower, but the Result Analyst role holds it, which would
		// show the board to the very people it monitors. Neither is right on its own, so
		// `superAdminOnly` closes the gap: the borrow decides who can reach the route at all, the
		// flag decides who sees it. Drop both once the dedicated key is granted.
		roleActions: [ACTIONS.RESULT_VERIFICATION],
		intendedRoleActions: [ACTIONS.ANALYST_PERFORMANCE],
		superAdminOnly: true,
		requiresElection: true,
		section: 'Results',
	},
	{
		icon: <VoterInteractionSVG />,
		title: 'Voter Interactions',
		pageUrl: pageurl.VOTER_INTERACTIONS,
		roleActions: [ACTIONS.VOTER_INTERACTION],
		section: 'Voter Outreach',
		requiresElection: true,
		sub: [
			{
				title: 'Messaging',
				url: 'messaging',
				roleActions: [ACTIONS.VOTER_INTERACTION_MESSAGING],
				requiresElection: true,
			},
		],
	},
	{
		icon: <ContactsSVG />,
		title: 'Contacts',
		pageUrl: pageurl.CONTACTS,
		// Gated on VOTER_INTERACTION until `contacts` is seeded in the backend role-action catalogue
		// (see actions.contacts.ts) — swap to ACTIONS.CONTACTS once the catalogue is synced.
		roleActions: [ACTIONS.VOTER_INTERACTION],
		intendedRoleActions: [ACTIONS.CONTACTS],
		section: 'Voter Outreach',
		// Deliberately NO requiresElection: contacts are scoped by the JWT UserId, not by election,
		// so gating on an election would hide the module whenever none is selected.
	},
	{
		icon: <MediaGallerySVG />,
		title: 'Media Gallery',
		pageUrl: pageurl.MEDIA_GALLERY,
		// Gated on VOTER_INTERACTION until `candidate_media` is seeded in the backend catalogue and
		// hand-granted to Party Admin + Candidate (see actions.candidate_media.ts for the full
		// ordering). Same borrow, same section, same reason as Contacts directly above.
		roleActions: [ACTIONS.VOTER_INTERACTION],
		intendedRoleActions: [ACTIONS.CANDIDATE_MEDIA],
		section: 'Voter Outreach',
		// Deliberately NO requiresElection: a library is scoped by (partyId, candidateId), and an
		// asset's election is an optional tag on the row rather than part of the key — so a library
		// is readable with no election selected.
	},
	{
		icon: <MessageTemplateSVG />,
		title: 'Message Templates',
		pageUrl: pageurl.MESSAGE_TEMPLATES,
		// Borrowed gate until `message_templates` is in the backend catalogue — see
		// actions.message_templates.ts. Same arrangement as Contacts and Media Gallery above.
		roleActions: [ACTIONS.VOTER_INTERACTION],
		intendedRoleActions: [ACTIONS.MESSAGE_TEMPLATES],
		section: 'Voter Outreach',
		// No requiresElection: a template belongs to its author, not to an election.
	},
	{
		icon: <ManageAgentsSVG />,
		title: 'Team',
		pageUrl: pageurl.MY_TEAM,
		// No action key means "has a team"; api.core gates MyTeam on these three ROLES, so the menu
		// does too. `roleActions` is the union of what each of them already holds, so the item still
		// fails closed for a resolved-but-empty permission set.
		roleActions: [
			ACTIONS.OVERVIEW,
			ACTIONS.OVERVIEW_PARTY_ADMIN,
			ACTIONS.OVERVIEW_PARTY_CANDIDATE,
			ACTIONS.ELECTION_OBSERVERS_MANAGE,
		],
		requiredRoles: ['Candidate', 'Party Admin', 'Observer Admin'],
		section: 'Account',
	},
	{
		icon: <MyWalletSVG />,
		title: 'My Wallet',
		pageUrl: pageurl.MYWALLET,
		// The owner's wallet: top-up and balance. A team member never sees it in P2 (P3 adds spending).
		excludedRoles: [DELEGATE_SUPPORT_ROLE],
		roleActions: [ACTIONS.MY_WALLET_VIEW],
		section: 'Finance',
	},
	{
		icon: <ElectiveCategorySVG />,
		title: 'Election Parameters',
		pageUrl: pageurl.ELECTIONPARAMETERS,
		roleActions: [ACTIONS.ELECTION_PARAMETERS],
		section: 'Election Setup',
		sub: [
			{
				title: 'Election Types',
				url: 'election-type',
				roleActions: [ACTIONS.ELECTION_PARAMETERS_TYPE],
			},
			{
				title: 'Election Categories',
				url: 'elective-category',
				roleActions: [ACTIONS.ELECTION_PARAMETERS_CATEGORY],
			},
			{
				title: 'Election Attributes',
				url: 'election-attribute',
				roleActions: [ACTIONS.ELECTION_PARAMETERS_ATTRIBUTES],
			},
			{
				// Reading the matrix is an attribute view; writing it is separately gated in-page on
				// `election_participants_manage`, so a viewer sees the timeline without the toggles.
				title: 'Party Participation',
				url: 'party-participation',
				roleActions: [ACTIONS.ELECTION_PARAMETERS_ATTRIBUTES],
			},
		],
	},
	{
		icon: <ElectionSVG />,
		title: 'Elections',
		pageUrl: pageurl.ELECTIONS,
		roleActions: [ACTIONS.ELECTION],
		section: 'Election Setup',
		sub: [
			{
				title: 'Dashboard',
				url: 'dashboard',
				roleActions: [ACTIONS.ELECTION],
			},
			{
				title: 'All Elections',
				url: '',
				roleActions: [ACTIONS.ELECTION],
			},
		],
	},
	{
		icon: <AISVG />,
		title: 'AI',
		pageUrl: pageurl.AI,
		roleActions: [ACTIONS.AI],
		hide: true,
		section: 'System',
	},
	{
		icon: <PoliticalPartySVG />,
		title: 'Political Party',
		pageUrl: pageurl.POLITICALPARTY,
		roleActions: [ACTIONS.ORGANIZATION],
		section: 'Directory',
	},
	{
		icon: <PoliticalPartySVG />,
		title: 'Developmental Partner',
		pageUrl: pageurl.ELECTIONOBSERVER,
		roleActions: [ACTIONS.ORGANIZATION],
		section: 'Directory',
	},
	{
		icon: <DivisionsSVG />,
		title: 'Constituent Metadata',
		pageUrl: pageurl.ELECTORALDIVISIONS,
		roleActions: [ACTIONS.ELECTORAL_DIVISION],
		section: 'Directory',
		sub: [
			{
				title: 'Geo-zones',
				url: 'zones',
				roleActions: [ACTIONS.ZONEVIEW],
			},
			{
				title: 'State',
				url: 'states',
				roleActions: [ACTIONS.STATEVIEW],
			},
			{
				title: 'Constituency',
				url: 'constituency',
				roleActions: [ACTIONS.CONSTITUENCYVIEW],
			},
			{
				title: 'LGA',
				url: 'lgas',
				roleActions: [ACTIONS.LGAVIEW],
			},
			{
				title: 'Polling Unit',
				url: 'polling-units',
				roleActions: [ACTIONS.POLLINGUNITVIEW],
			},
			{
				title: 'RA/Ward',
				url: 'wards',
				roleActions: [ACTIONS.WARDVIEW],
			},
			{
				title: 'Tree',
				url: 'tree',
				roleActions: [ACTIONS.ELECTORAL_DIVISION],
				superAdminOnly: true,
			},
		],
	},
	{
		icon: <ImportVotersSVG />,
		title: 'Voters',
		pageUrl: pageurl.VOTERS,
		// OR-gate: the new key, or the older frontend-only import key, so nobody who imports today
		// loses the page on deploy. The Register tab additionally requires Super Admin.
		roleActions: [ACTIONS.VOTERS_VIEW, ACTIONS.IMPORT_VOTERS],
		section: 'Directory',
	},
	{
		icon: <CandidateRecordsSVG />,
		title: 'Candidate Records',
		pageUrl: pageurl.CANDIDATE_RECORDS,
		// The INEC registry: reads are open to any signed-in user server-side and every write is
		// Super Admin by ROLE, so there is no backend action to gate on. ORGANIZATION is the borrow
		// (the Political Party precedent in this section) and `superAdminOnly` narrows it — the
		// Analyst Performance shape. The page gates itself on Super Admin as well, because the
		// route guard only observes.
		roleActions: [ACTIONS.ORGANIZATION],
		intendedRoleActions: [ACTIONS.CANDIDATE_RECORDS],
		superAdminOnly: true,
		section: 'Directory',
	},
	{
		icon: <SituationRoomSVG />,
		title: 'Situation Room',
		pageUrl: pageurl.VESR,
		roleActions: ANY_VESR_ACTION,
		section: 'Situation Room',
		sub: [
			{ title: 'Dashboard', url: '', roleActions: [ACTIONS.VESR_DASHBOARD] },
			{ title: 'Incidents', url: 'incidents', roleActions: [ACTIONS.VESR_INCIDENTS] },
			{ title: 'Election Pulse', url: 'election-pulse', roleActions: [ACTIONS.VESR_ELECTION_PULSE] },
			{ title: 'Communication', url: 'communication', roleActions: [ACTIONS.VESR_COMMUNICATION] },
			{ title: 'Media', url: 'media', roleActions: [ACTIONS.VESR_MEDIA] },
			{ title: 'Integration', url: 'integration', roleActions: [ACTIONS.VESR_INTEGRATION] },
			{ title: 'Activities', url: 'activities', roleActions: [ACTIONS.VESR_DASHBOARD] },
			{ title: 'Analytics', url: 'analytics', roleActions: [ACTIONS.VESR_ANALYTICS] },
			{ title: 'Feedback', url: 'feedback', roleActions: [ACTIONS.VESR_FEEDBACK] },
			{ title: 'Reports', url: 'reports', roleActions: [ACTIONS.VESR_REPORTS] },
			{ title: 'Users', url: 'users', roleActions: [ACTIONS.VESR_USERS] },
			{ title: 'Audit Logs', url: 'audit-logs', roleActions: [ACTIONS.VESR_AUDIT_LOGS] },
		],
	},
	{
		icon: <VerificationRequestSVG />,
		title: 'System Management',
		pageUrl: pageurl.MANAGEMENT,
		roleActions: [ACTIONS.SYSTEM_MANAGEMENT],
		section: 'System',
		sub: [
			{
				title: 'Dashboard',
				url: 'dashboard',
				roleActions: [ACTIONS.SYSTEM_MANAGEMENT],
			},
			{
				title: 'Users',
				url: 'users',
				roleActions: [ACTIONS.SYSTEM_MANAGEMENT_USERS],
			},
			{
				title: 'Role Actions',
				url: 'role-actions',
				roleActions: [ACTIONS.SYSTEM_MANAGEMENT_ROLE_ACTION],
			},
			{
				title: 'Roles',
				url: 'roles',
				roleActions: [ACTIONS.SYSTEM_MANAGEMENT_ROLES],
			},
			{
				title: 'Roles × Domains matrix',
				url: 'roles/matrix',
				roleActions: [ACTIONS.SYSTEM_MANAGEMENT_ROLES],
			},
			{
				title: 'API Keys',
				url: 'api-keys',
				roleActions: [ACTIONS.SYSTEM_MANAGEMENT_API_KEYS],
			},
			{
				// api.bot gates every ServiceRegistry endpoint on the Super Admin ROLE (not an action
				// key), so the menu mirrors that rather than minting a key api.core would have to seed.
				title: 'Service Registry',
				url: 'service-registry',
				roleActions: [ACTIONS.SYSTEM_MANAGEMENT],
				superAdminOnly: true,
			},
			{
				// api.core `TeamAccessController` is `[Authorize(Roles = "Super Admin")]` — a role, not an
				// action key — so this mirrors Service Registry: borrow the section gate, narrow by role.
				title: 'Team access',
				url: 'team-access',
				roleActions: [ACTIONS.SYSTEM_MANAGEMENT],
				superAdminOnly: true,
			},
			{
				title: 'Service Rates',
				url: 'service-rates',
				roleActions: [ACTIONS.SYSTEM_MANAGEMENT_SERVICE_RATES],
			},
			{
				title: 'Coupons',
				url: 'coupons',
				roleActions: [ACTIONS.SYSTEM_MANAGEMENT_COUPONS],
			},
			{
				title: 'Wallets',
				url: 'wallets',
				roleActions: [ACTIONS.SYSTEM_MANAGEMENT_WALLETS],
			},
			{
				title: 'Feedback',
				url: 'feedback',
				roleActions: [ACTIONS.SYSTEM_MANAGEMENT_FEEDBACK],
			},
			// Legal — gated on the parent SYSTEM_MANAGEMENT code (like Dashboard) so the
			// items are visible to System Management admins immediately. Once the
			// dedicated `system_management_legal_*` codes are synced into the backend
			// permission catalogue, tighten these to the granular codes.
			{
				title: 'Document Types',
				url: 'document-types',
				roleActions: [ACTIONS.SYSTEM_MANAGEMENT],
				intendedRoleActions: [ACTIONS.SYSTEM_MANAGEMENT_LEGAL_DOCUMENT_TYPES],
			},
			{
				title: 'Legal Documents',
				url: 'legal-documents',
				roleActions: [ACTIONS.SYSTEM_MANAGEMENT],
				intendedRoleActions: [ACTIONS.SYSTEM_MANAGEMENT_LEGAL_DOCUMENTS],
			},
			{
				title: 'Consent Audit',
				url: 'legal-consents',
				roleActions: [ACTIONS.SYSTEM_MANAGEMENT],
				intendedRoleActions: [ACTIONS.SYSTEM_MANAGEMENT_LEGAL_CONSENTS],
			},
			{
				title: 'Channel Governance',
				url: 'channel-governance',
				roleActions: [ACTIONS.SYSTEM_MANAGEMENT_CHANNEL_GOVERNANCE],
			},
			// Borrowed gate, like Legal above: `system_management_lms_courses` is not in the backend
			// catalogue yet, and a sole gate on it would hide the item from everyone.
			{
				title: 'Academy Courses',
				url: 'academy-courses',
				roleActions: [ACTIONS.SYSTEM_MANAGEMENT],
				intendedRoleActions: [ACTIONS.SYSTEM_MANAGEMENT_LMS_COURSES],
			},
		],
	},
];
